import React, { useEffect, useState } from 'react';

import {
  IGetResReadReceiptDetail,
  IReadReceiptItem,
  ReadReceiptSubjectType,
} from '../../../frontend-libs/evlapp-types';
import { t } from '../../i18n';
import { getReadReceiptDetail } from '../../services/ReadReceiptService';
import { formatDateTime } from '../../utils/Date/Date';
import { getProfileName } from '../../utils/Profile/profile';
import { Modal } from '../Modal/Modal';
import { Text } from '../Text/Text';

interface ReadReceiptModalProps {
  subjectType: ReadReceiptSubjectType;
  subjectId: string;
  isShown: boolean;
  onHide: () => void;
}

const namespace = 'ReadReceipt.Modal';

const ReadReceiptRow: React.FC<{ item: IReadReceiptItem }> = ({ item }) => (
  <div className="ReadReceiptModal__row">
    <Text as="span" size="body-medium" weight="500" color="Neutral750">
      {getProfileName(item.profile)}
    </Text>
    {item.readAt !== null ? (
      <Text as="span" size="body-small" color="Primary600">
        {formatDateTime(item.readAt)}
      </Text>
    ) : (
      <Text as="span" size="body-small" color="SystemRed">
        {item.profile.isUser ? t('unread', namespace) : t('noAccount', namespace)}
      </Text>
    )}
  </div>
);

export const ReadReceiptModal: React.FC<ReadReceiptModalProps> = (props) => {
  const { subjectType, subjectId, isShown, onHide } = props;

  const [detail, setDetail] = useState<IGetResReadReceiptDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isShown || detail !== null) return;
    setIsLoading(true);
    getReadReceiptDetail(subjectType, subjectId)
      .then((res) => setDetail(res))
      .catch(() => undefined)
      .finally(() => setIsLoading(false));
  }, [isShown, detail, subjectType, subjectId]);

  const read = detail?.receipts.filter((item) => item.readAt !== null) ?? [];
  const unread = detail?.receipts.filter((item) => item.readAt === null) ?? [];

  return (
    <Modal
      isShown={isShown}
      onHide={onHide}
      isLoading={isLoading}
      size="small"
      className="ReadReceiptModal"
      enableOutsideClose
    >
      <Text as="h2" size="h2" className="mb-1">
        {t('title', namespace)}
      </Text>
      <Text as="p" size="body-small" color="Neutral600" className="mb-2">
        {t('subtitle', namespace)}
      </Text>

      <Text as="h3" size="h3" className="ReadReceiptModal__heading">
        {t('read', namespace)} ({read.length})
      </Text>
      {read.length === 0 ? (
        <Text as="p" size="body-small" color="Neutral600" className="ReadReceiptModal__list">
          {t('empty', namespace)}
        </Text>
      ) : (
        <div className="ReadReceiptModal__list">
          {read.map((item) => (
            <ReadReceiptRow key={item.profile.id} item={item} />
          ))}
        </div>
      )}

      {unread.length > 0 && (
        <>
          <Text as="h3" size="h3" className="ReadReceiptModal__heading">
            {t('unread', namespace)} ({unread.length})
          </Text>
          <div className="ReadReceiptModal__list">
            {unread.map((item) => (
              <ReadReceiptRow key={item.profile.id} item={item} />
            ))}
          </div>
        </>
      )}
    </Modal>
  );
};
